/**
 * The organization's staff accounts: who can sign in to this CRM, as what, and when they last did.
 *
 *   * ADMIN-ONLY. Adding, changing and resetting are refused to a technician server-side, so the
 *     sidebar hides the entry and a deep link renders a sentence instead of an empty list.
 *   * NO DELETE. Disabling refuses the account at sign-in and on its next request, and keeps every
 *     timer, time entry and audit row that names it; Enable undoes it.
 *   * ONE EDITOR AND ONE PASSWORD FORM, not one per row: rows are rebuilt on every refresh, which
 *     would discard a half-typed password. Both are emptied on open and close.
 *   * Host operators working in this organization are not listed: their stand-in rows have no
 *     password and are managed from the host's Organizations page.
 */
import type { Container } from 'webwin';
import { currentUser, isAdmin } from '../api/auth.js';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import { StaffRoles, type UserDto } from '../contracts.gen.js';
import { join, localDate, localDateTime, plural } from '../format.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { confirm } from '../ui/dialogs.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkLine,
  comboBox,
  field,
  fieldRow,
  FONT,
  GAP,
  h1,
  h2,
  inlineRow,
  muted,
  passwordBox,
  relayout,
  scrollPage,
  stackOf,
  text,
  textBox,
  value,
  vstack,
  wrappingCheck
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

const ROLES = [
  { role: StaffRoles.Staff, label: 'Technician' },
  { role: StaffRoles.Admin, label: 'Administrator' }
] as const;

const roleLabel = (role: string): string => (role === StaffRoles.Admin ? 'Administrator' : 'Technician');

export function usersPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      const { view, content } = scrollPage();
      host.controls.add(view);

      const header = stackOf(
        vstack({ spacing: GAP.sm }),
        h1('Users'),
        muted('The staff accounts that sign in to this organization, and what each may do.')
      );
      content.controls.add(header);

      if (!isAdmin()) {
        const notice = card();
        notice.controls.add(
          text('Managing staff accounts is administrator-only. To change your own password, use Change password in the sidebar.')
        );
        content.controls.add(notice);
        relayout(content);
        return;
      }

      let busy = false;
      let users: UserDto[] = [];
      const status = new StatusLine();
      header.controls.add(stackOf(buttonRow(), button('Add user', () => openEditor(null), true), button('Refresh', () => void load())));
      header.controls.add(status.label);

      // ---- add / edit ----
      let editing: UserDto | null = null;
      const editorTitle = h2('Add user');
      const nameBox = textBox({ placeholder: 'Their name' });
      const emailBox = textBox({ placeholder: 'name@example.com' });
      const roleCombo = comboBox(ROLES, { itemText: r => r.label, selectedIndex: 0 });
      const roleHint = muted('');
      const tempPassword = passwordBox();
      const mustChange = wrappingCheck('Make them choose their own password at first sign-in', true);
      const createOnly = stackOf(vstack({ spacing: GAP.md }), field('Temporary password', tempPassword), checkLine(mustChange));
      const emailField = field('Email', emailBox);
      const editorStatus = new StatusLine();
      const saveButton = button('Add', () => void save(), true);
      const editor = card({ visible: false });
      editor.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          editorTitle,
          fieldRow(field('Name', nameBox), emailField),
          field('Role', roleCombo),
          roleHint,
          createOnly,
          stackOf(buttonRow(), saveButton, button('Cancel', () => closeEditor())),
          editorStatus.label
        )
      );
      roleCombo.on('selectionchanged', () => describeRole());

      // ---- reset password ----
      let resetting: UserDto | null = null;
      const resetTitle = h2('Reset password');
      const newPassword = passwordBox();
      const confirmPassword = passwordBox();
      const resetMustChange = wrappingCheck('Make them choose their own password at next sign-in', true);
      const resetStatus = new StatusLine();
      const resetCard = card({ visible: false });
      resetCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          resetTitle,
          fieldRow(field('New password', newPassword), field('Confirm', confirmPassword)),
          checkLine(resetMustChange),
          muted('Every session of theirs is signed out, and a sign-in lockout on the address is released.'),
          stackOf(buttonRow(), button('Reset password', () => void resetPassword(), true), button('Cancel', () => closeReset())),
          resetStatus.label
        )
      );

      // ---- list ----
      const countLine = muted('');
      const list = new CardList<UserDto>({
        key: u => u.id,
        build: u => buildRow(u)
      });
      const listCard = card();
      listCard.controls.add(stackOf(vstack({ spacing: GAP.sm }), countLine, list.panel));

      content.controls.add(stackOf(vstack({ spacing: GAP.lg }), editor, resetCard, listCard));

      // ---------------------------------------------------------------

      function buildRow(u: UserDto): Card {
        const me = u.id === currentUser()?.id;
        const heading = inlineRow();
        heading.controls.add(value(u.displayName, { font: FONT.bold }));
        heading.controls.add(chip(roleLabel(u.role), u.role === StaffRoles.Admin ? 'brand' : 'neutral'));
        if (u.disabled) heading.controls.add(chip('Disabled', 'danger'));
        if (u.mustChangePassword) heading.controls.add(chip('Must change password', 'warn'));
        if (me) heading.controls.add(chip('you', 'info'));

        const actions = buttonRow();
        actions.controls.add(button('Edit', () => openEditor(u)));
        actions.controls.add(button('Reset password', () => openReset(u)));
        if (!me) actions.controls.add(button(u.disabled ? 'Enable' : 'Disable', () => void setDisabled(u, !u.disabled)));

        const body = stackOf(
          vstack({ spacing: GAP.xs }),
          heading,
          text(u.email, { font: FONT.small }),
          muted(join([
            u.lastSignInUtc ? 'Last signed in ' + localDateTime(u.lastSignInUtc) : 'Never signed in',
            'added ' + localDate(u.createdUtc)
          ])),
          actions
        );
        const shell = card({ tint: u.disabled ? 'alt' : undefined });
        shell.controls.add(body);
        return shell;
      }

      function describeRole(): void {
        roleHint.text =
          roleCombo.selectedItem?.role === StaffRoles.Admin
            ? 'Administrators also manage settings, security, the audit log and these accounts.'
            : 'Technicians work with customers, devices, tickets and billing, and can read the settings.';
        relayout(roleHint);
      }

      function openEditor(u: UserDto | null): void {
        closeReset();
        editing = u;
        editorTitle.text = u ? `Edit ${u.displayName}` : 'Add user';
        saveButton.text = u ? 'Save' : 'Add';
        nameBox.text = u?.displayName ?? '';
        emailBox.text = u?.email ?? '';
        emailField.visible = u === null;
        createOnly.visible = u === null;
        roleCombo.selectedIndex = Math.max(0, ROLES.findIndex(r => r.role === (u?.role ?? StaffRoles.Staff)));
        tempPassword.text = '';
        mustChange.checked = true;
        editorStatus.clear();
        describeRole();
        editor.visible = true;
        relayout(content);
        nameBox.focus();
      }

      function closeEditor(): void {
        editing = null;
        tempPassword.text = '';
        editor.visible = false;
        relayout(content);
      }

      function openReset(u: UserDto): void {
        closeEditor();
        resetting = u;
        resetTitle.text = `Reset the password of ${u.displayName}`;
        newPassword.text = '';
        confirmPassword.text = '';
        resetMustChange.checked = true;
        resetStatus.clear();
        resetCard.visible = true;
        relayout(content);
        newPassword.focus();
      }

      function closeReset(): void {
        resetting = null;
        newPassword.text = '';
        confirmPassword.text = '';
        resetCard.visible = false;
        relayout(content);
      }

      async function save(): Promise<void> {
        if (busy) return;
        const role = roleCombo.selectedItem?.role ?? StaffRoles.Staff;
        const target = editing;
        if (target && role !== target.role && target.id === currentUser()?.id) {
          editorStatus.error('You cannot change your own role. Ask another administrator.');
          return;
        }
        busy = true;
        editorStatus.busy(target ? 'Saving…' : 'Adding…');
        try {
          if (target) {
            const saved = await crm.updateStaffUser(target.id, { displayName: nameBox.text.trim(), role, disabled: null });
            if (ctx.cancelled()) return;
            status.ok(`Saved ${saved.displayName}.`);
          } else {
            const added = await crm.createStaffUser({
              email: emailBox.text.trim(),
              displayName: nameBox.text.trim(),
              role,
              password: tempPassword.text,
              mustChangePassword: mustChange.checked
            });
            if (ctx.cancelled()) return;
            status.ok(
              `Added ${added.email} as ${roleLabel(added.role).toLowerCase()}. Give them the temporary password; ` +
                (added.mustChangePassword ? 'they choose their own at first sign-in.' : 'they can sign in with it straight away.')
            );
          }
          closeEditor();
          await load();
        } catch (error) {
          // The form stays open with what was typed, so a taken address is fixed rather than retyped.
          if (!ctx.cancelled()) editorStatus.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function resetPassword(): Promise<void> {
        if (busy || !resetting) return;
        if (newPassword.text !== confirmPassword.text) {
          resetStatus.error('The two passwords do not match.');
          return;
        }
        busy = true;
        resetStatus.busy('Resetting…');
        try {
          const saved = await crm.resetStaffPassword(resetting.id, {
            password: newPassword.text,
            mustChangePassword: resetMustChange.checked
          });
          if (ctx.cancelled()) return;
          closeReset();
          status.ok(`Reset the password of ${saved.displayName}. Their sessions were signed out.`);
          await load();
        } catch (error) {
          if (!ctx.cancelled()) resetStatus.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function setDisabled(u: UserDto, disabled: boolean): Promise<void> {
        if (disabled) {
          const ok = await confirm(ctx.desktop, {
            title: `Disable ${u.displayName}`,
            message: `${u.email} will be refused at sign-in, and any session they have open ends on its next request. Their time entries and history are kept, and Enable gives the account back.`
          });
          if (!ok || ctx.cancelled()) return;
        }
        if (busy) return;
        busy = true;
        status.busy(disabled ? 'Disabling…' : 'Enabling…');
        try {
          const saved = await crm.updateStaffUser(u.id, { displayName: null, role: null, disabled });
          if (ctx.cancelled()) return;
          status.ok(`${saved.displayName} is ${saved.disabled ? 'disabled' : 'enabled'}.`);
          await load();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function load(): Promise<void> {
        try {
          users = await crm.getStaffUsers(ctx.dispose.signal());
          if (ctx.cancelled()) return;
          const disabled = users.filter(u => u.disabled).length;
          const admins = users.filter(u => u.role === StaffRoles.Admin && !u.disabled).length;
          countLine.text = join([
            plural(users.length, 'account'),
            plural(admins, 'enabled administrator'),
            disabled > 0 ? `${disabled} disabled` : null
          ]);
          list.set(users);
          relayout(content);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the users: ' + describeError(error));
        }
      }

      void load();
    }
  };
}
